import React, { useEffect, useState, useRef, useMemo, createContext, useContext } from 'react';
import { Animated, StyleSheet, useWindowDimensions } from 'react-native';
import { Modal } from '@ui-kitten/components';
// [TODO] - Support non-full screen items by calculating height
// We cannot get the content height until after Modal mounts it, so it's a
// bit awkward to get the height through the normal lifecycle
// We probably have to resort to a setTimeout to support this
// For now, we can just take a prop value to set the height
// And shift resposibility to the parent component
const ANIM_DURATION = 600;
const S = StyleSheet.create({
    modalFullscreen: {
        height: '100vh',
        width: '100vw',
    },
    animContainer: {
        // @ts-expect-error -- Web only position
        position: 'fixed',
        flex: 1,
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
    },
    visible: {
        display: 'flex',
    },
    nonVisible: {
        display: 'none',
    },
});
const DNASliderContext = createContext({
    visible: false,
    setVisible: () => { },
});
export const useDNASlider = () => useContext(DNASliderContext);
export var DNASliderOrientationEnum;
(function (DNASliderOrientationEnum) {
    DNASliderOrientationEnum["vertical"] = "vertical";
    DNASliderOrientationEnum["horizontal"] = "horizontal";
})(DNASliderOrientationEnum || (DNASliderOrientationEnum = {}));
export const DNASliderOrientationKeys = Object.keys(DNASliderOrientationEnum);
const DNASlider = (props) => {
    const { visible, setVisible, children, orientation = 'vertical', skipInitialAnimation, sliderSize, onVisible, onInvisible, invertAnimation, style, ...modalProps } = props;
    const [appear, setAppear] = useState(false);
    const anim = useRef(new Animated.Value(skipInitialAnimation ? 1 : 0)).current;
    const skippedAnim = useRef(false);
    const contentRef = useRef();
    const dimensions = useWindowDimensions();
    useEffect(() => {
        if (visible)
            setAppear(true);
        if (skipInitialAnimation && !skippedAnim.current) {
            skippedAnim.current = true;
            return;
        }
        if (!visible && !appear) {
            return;
        }
        Animated
            .timing(anim, {
            toValue: visible ? 1 : 0,
            duration: ANIM_DURATION,
            useNativeDriver: false,
        })
            .start(res => {
            const nextState = visible || !res.finished;
            setAppear(nextState);
            nextState
                ? onVisible?.()
                : onInvisible?.();
        });
    }, [visible]);
    const translateStyle = useMemo(() => {
        const boundary = orientation === 'vertical'
            ? dimensions.height
            : dimensions.width;
        const transformAxis = orientation === 'vertical'
            ? 'translateY'
            : 'translateX';
        const outputRange = invertAnimation
            ? [sliderSize ? -sliderSize : -boundary, 0]
            : [boundary, sliderSize ? boundary - sliderSize : 0];
        return {
            transform: [{
                    [transformAxis]: anim.interpolate({
                        inputRange: [0, 1],
                        outputRange: outputRange,
                    }),
                }],
        };
    }, [dimensions, sliderSize, orientation, invertAnimation]);
    const isVisible = (visible || appear);
    const values = {
        visible,
        setVisible,
    };
    // NOTE: This throws a React console error whenever due composing the children
    // Should not be a big issue though
    return (
    /**
     * Suggest switching to react-native-modal here. Seems like a decently
     * maintained & flexible enhancement to the default react-native modal
    * https://github.com/react-native-modal/react-native-modal
    */
    <Modal visible={isVisible} {...modalProps}>
      <DNASliderContext.Provider value={values}>
        <Animated.View ref={contentRef} style={[
        style,
        S.animContainer,
        isVisible ? S.visible : S.nonVisible,
        translateStyle,
    ]}>
          {children}
        </Animated.View>
      </DNASliderContext.Provider>
    </Modal>);
};
export default DNASlider;
//# sourceMappingURL=DNASlider.js.map